import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Chip, Grid, LinearProgress, Typography } from "@mui/material";
import { AppDialog } from "components/common/dialogs";
import { AppAutocomplete, AppTextField } from "components/common/inputs";
import { AppButton } from "components/common/buttons";
import { formatAmount, formatCurrencyWithUnit, formatDate, formatQuantity } from "utils/formatters";
import contractItemsApi from "../../services/contractItems.service";
import usePaymentItemScopes from "./usePaymentItemScopes";
import { indexPaymentItems, paymentIdOf } from "./paymentScopes";

const emptyForm = { contractItemId: "", paymentQuantity: "", notes: "" };
const renderChoice = ({ key, ...props }, option) => <li key={option.value ?? key} {...props}>{option.label}</li>;
const isPayableItem = (item) => !item.hasChildren && !item.children?.length
  && Number(item.contractQuantity) > 0 && Number(item.unitPrice) > 0
  && Number(item.contractAmount) > 0 && Boolean(item.unit?.trim());

export default function ContractPaymentModal({ open, onClose, onSave, paymentProfile,
  contractItems = [], allContractPayments = [], editingPayment = null, preferredScopeId }) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [committed, setCommitted] = useState(false);
  const itemIndex = useMemo(() => indexPaymentItems(contractItems), [contractItems]);
  const editingItem = editingPayment ? itemIndex.get(editingPayment.contractItemId) : null;
  const itemScopes = usePaymentItemScopes({ open, contractId: paymentProfile?.contractId,
    items: contractItems, preferredScopeId: editingItem?.contractItemScopeId ?? preferredScopeId, payableOnly: true });
  const scopedItems = useMemo(() => [...indexPaymentItems(itemScopes.items).values()], [itemScopes.items]);
  const selectedItem = itemIndex.get(Number(form.contractItemId));
  const editingId = editingPayment ? paymentIdOf(editingPayment) : null;
  const paidQuantityFor = (itemId) => allContractPayments.reduce((sum, payment) =>
    Number(payment.contractItemId) === Number(itemId)
      && !(editingId != null && Number(paymentIdOf(payment)) === Number(editingId))
      ? sum + Number(payment.paymentQuantity || 0) : sum, 0);
  const remainingFor = (item) => Math.max(0, Math.round((Number(item.contractQuantity || 0) - paidQuantityFor(item.itemId)) * 10000) / 10000);
  const itemOptions = scopedItems.filter((item) => isPayableItem(item) && remainingFor(item) > 0)
    .map((item) => ({ value: item.itemId, label: (item.itemCode || "") + " - " + item.itemName }));
  const selectable = Boolean(selectedItem && itemOptions.some((option) => option.value === selectedItem.itemId)
    && !itemScopes.readOnly && !itemScopes.loading && !itemScopes.error);
  const amount = selectedItem && Number.isFinite(Number(form.paymentQuantity))
    ? Math.round(Number(selectedItem.unitPrice) * Number(form.paymentQuantity)) : 0;

  useEffect(() => {
    if (!open) return;
    setForm(editingPayment ? { contractItemId: editingPayment.contractItemId,
      paymentQuantity: editingPayment.paymentQuantity, notes: editingPayment.notes || "" } : emptyForm);
    setErrors({});
    setCommitted(false);
  }, [open, editingPayment]);

  const changeScope = (change, value) => {
    change(value ? Number(value) : null);
    setForm((previous) => ({ ...previous, contractItemId: "", paymentQuantity: "" }));
    setErrors({});
  };
  const changeField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value,
      ...(field === "contractItemId" ? { paymentQuantity: "" } : {}) }));
    setErrors({});
  };
  const handleClose = () => { if (!saving) onClose(); };
  const handleSave = async () => {
    if (saving || committed) return;
    const nextErrors = {};
    const quantity = Number(form.paymentQuantity);
    if (!selectable) nextErrors.contractItemId = "Vui lòng chọn hạng mục trong phạm vi được phép thanh toán.";
    if (!Number.isFinite(quantity) || quantity < 0.01) nextErrors.paymentQuantity = "Khối lượng thanh toán phải từ 0,01 trở lên.";
    else if (selectedItem && quantity > remainingFor(selectedItem))
      nextErrors.paymentQuantity = "Khối lượng không được vượt quá khối lượng còn lại (" + formatQuantity(remainingFor(selectedItem)) + ").";
    if (Object.keys(nextErrors).length) { setErrors(nextErrors); return; }
    setSaving(true);
    try {
      await onSave({ paymentProfileId: paymentProfile.paymentProfileId, contractItemId: selectedItem.itemId,
        paymentQuantity: quantity, paymentAmount: amount, notes: form.notes.trim() || null }, editingId ?? undefined);
      setCommitted(true);
      // Preserve the existing derived completion update without retrying a committed payment.
      try {
        const totalPaid = paidQuantityFor(selectedItem.itemId) + quantity;
        await contractItemsApi.updateCompletionPercentage(selectedItem.itemId,
          Math.min(100, Math.round(totalPaid / Number(selectedItem.contractQuantity) * 100)));
        onClose();
      } catch {
        setErrors({ submit: "Thanh toán đã lưu nhưng chưa cập nhật được tỷ lệ hoàn thành. Hãy tải lại để kiểm tra; không thêm lại thanh toán." });
      }
    } catch (error) {
      setErrors({ submit: error.response?.data?.message || error.message || "Không thể lưu chi tiết thanh toán." });
    } finally { setSaving(false); }
  };

  if (!paymentProfile) return null;
  const locked = saving || committed;
  return <AppDialog open={open} onClose={handleClose} loading={saving} size="md"
    title={editingPayment ? "Chỉnh sửa chi tiết thanh toán" : "Thêm chi tiết thanh toán"}
    subtitle="Chọn loại hạng mục, tỉnh/xã rồi chọn hạng mục cần thanh toán."
    actions={<>
      <AppButton variant="outlined" color="inherit" disabled={saving} onClick={handleClose}>{committed ? "Đóng" : "Hủy"}</AppButton>
      {!committed && <AppButton variant="contained" disabled={saving || !selectable} onClick={handleSave}>
        {editingPayment ? "Cập nhật" : "Thêm mới"}
      </AppButton>}
    </>}>
    <Box sx={{ mb: 2, p: 1, bgcolor: "#f8fafc", borderRadius: 1, border: "1px solid #e0e0e0" }}>
      <Typography fontWeight={650}>{paymentProfile.paymentName}</Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 1 }}>
        <Typography variant="body2">Ngày thanh toán: {formatDate(paymentProfile.paymentDate)}</Typography>
        <Chip label={"VAT: " + paymentProfile.vat + "%"} size="small" />
      </Box>
    </Box>
    {itemScopes.loading ? <LinearProgress aria-label="Đang tải phân cấp thanh toán" />
      : itemScopes.error ? <Alert severity="error" action={<AppButton onClick={itemScopes.retry}>Thử lại</AppButton>}>{itemScopes.error}</Alert>
      : <Grid container spacing={2}>
        {itemScopes.sections.length > 0 && <>
          <Grid size={{ xs: 12 }}><AppAutocomplete label="Phân cấp hạng mục" required
            renderOption={renderChoice}
            value={itemScopes.section?.contractSectionId || ""} disabled={locked || !!editingPayment}
            options={itemScopes.sections.filter((section) => section.isActive !== false || section.contractSectionId === itemScopes.section?.contractSectionId)
              .map((section) => ({ value: section.contractSectionId, label: section.sectionName }))}
            onChange={(value) => changeScope(itemScopes.changeSection, value)} /></Grid>
          <Grid size={{ xs: 12 }}><AppAutocomplete label="Phạm vi" required
            renderOption={renderChoice}
            value={itemScopes.scope?.contractItemScopeId || ""} disabled={locked || !!editingPayment || !itemScopes.scopes.length}
            options={itemScopes.scopes.map((scope) => ({ value: scope.contractItemScopeId, label: scope.scopeName }))}
            onChange={(value) => changeScope(itemScopes.changeScope, value)}
            helperText="Scope Tổng hợp không được chọn để thanh toán." /></Grid>
        </>}
        {itemScopes.readOnly && <Grid size={{ xs: 12 }}><Alert severity="info">Phạm vi này chỉ cho phép xem.</Alert></Grid>}
        <Grid size={{ xs: 12 }}><AppAutocomplete label="Hạng mục thanh toán" required
          renderOption={renderChoice}
          value={form.contractItemId} options={itemOptions} disabled={locked || !!editingPayment || itemScopes.readOnly || !itemOptions.length}
          placeholder="Tìm theo mã hoặc tên hạng mục..." onChange={(value) => changeField("contractItemId", value ? Number(value) : "")}
          error={!!errors.contractItemId} helperText={errors.contractItemId}
          noOptionsText="Không có hạng mục còn khối lượng để thanh toán" /></Grid>
        {!itemOptions.length && <Grid size={{ xs: 12 }}><Alert severity="info">Không có hạng mục còn khối lượng để thanh toán trong phạm vi này.</Alert></Grid>}
        {editingPayment && <Grid size={{ xs: 12 }}><Typography variant="caption" color="text.secondary">
          Hạng mục của dòng đã lưu được giữ nguyên. Để đổi hạng mục, hãy xóa dòng cũ và thêm chi tiết mới.
        </Typography></Grid>}
        {selectedItem && <Grid size={{ xs: 12 }}><Typography variant="body2" color="text.secondary">
          Đơn giá: {formatCurrencyWithUnit(selectedItem.unitPrice)} · Khối lượng còn lại: {formatQuantity(remainingFor(selectedItem))} {selectedItem.unit}
        </Typography></Grid>}
        <Grid size={{ xs: 12, sm: 6 }}><AppTextField label="Khối lượng thanh toán" type="number" required
          value={form.paymentQuantity} disabled={locked || !selectable} onChange={(value) => changeField("paymentQuantity", value)}
          inputProps={{ min: 0.01, step: 0.0001 }} error={!!errors.paymentQuantity}
          helperText={errors.paymentQuantity || (selectedItem ? "Đơn vị: " + selectedItem.unit : "")} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><AppTextField label="Thành tiền (trước thuế)" value={formatAmount(amount)} disabled /></Grid>
        <Grid size={{ xs: 12 }}><AppTextField label="Ghi chú" value={form.notes} multiline minRows={3}
          disabled={locked} onChange={(value) => changeField("notes", value)} /></Grid>
      </Grid>}
    {errors.submit && <Alert severity={committed ? "warning" : "error"} sx={{ mt: 2 }}>{errors.submit}</Alert>}
  </AppDialog>;
}
